/* Macro & Trade dashboard. Reads four static JSON files written by server-side collectors
   (macro.json FRED, agsi.json GIE, portwatch.json IMF PortWatch, ofac.json OFAC). Every section renders on its own,
   so one missing or failing feed never blanks the page. All text is inserted with textContent. */
(function () {
    const root = document.querySelector('[data-macro-trade]');
    if (!root) return;

    const NS = 'http://www.w3.org/2000/svg';
    const q = function (s) { return root.querySelector(s); };
    const els = {
        stats: q('[data-mt-stats]'), gas: q('[data-mt-gas]'), gasAsof: q('[data-mt-gas-asof]'),
        choke: q('[data-mt-choke]'), chokeAsof: q('[data-mt-choke-asof]'), chokeSort: q('[data-mt-choke-sort]'),
        filter: q('[data-mt-filter]'), banks: q('[data-mt-banks]'), banksAsof: q('[data-mt-banks-asof]'),
        sx: q('[data-mt-sanctions]'), sxAsof: q('[data-mt-sanctions-asof]'), srcs: q('[data-mt-srcs]'),
        mkBox: q('[data-mt-markets-box]'), mk: q('[data-mt-markets]'), mkAsof: q('[data-mt-markets-asof]'),
        dcBox: q('[data-mt-daily-box]'), dc: q('[data-mt-daily]'), dcAsof: q('[data-mt-daily-asof]'),
        stBox: q('[data-mt-stocks-box]'), st: q('[data-mt-stocks]'), stAsof: q('[data-mt-stocks-asof]'),
        cmBox: q('[data-mt-comm-box]'), cm: q('[data-mt-comm]'), cmAsof: q('[data-mt-comm-asof]')
    };
    const REGIONS = [['all', 'All regions'], ['americas', 'Americas'], ['eu', 'Europe'], ['asia', 'Asia'],
        ['me', 'Middle East'], ['africa', 'Africa'], ['russia', 'Russia']];
    const TAGNAME = { americas: 'Americas', eu: 'Europe', asia: 'Asia', me: 'Middle East', africa: 'Africa', russia: 'Russia', global: 'Global' };
    const KIND = { designation: 'Designation', removal: 'Removal', license: 'License', enforcement: 'Enforcement', other: 'Other' };
    const HIDE_AFTER_DAYS = 400;      // a series this stale (e.g. a policy rate last updated in 2022) is not shown at all
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const D = { macro: null, agsi: null, pw: null, ofac: null, mk: null, cm: null, st: null, dc: null };
    let region = 'all', chokeSort = 'traffic';

    // ---------- helpers ----------
    function h(tag, cls, text, parent) {
        const n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        if (parent) parent.appendChild(n);
        return n;
    }
    function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
    function safeUrl(u) { return /^https?:\/\//i.test(u) ? u : null; }
    function num(v) { return typeof v === 'number' && isFinite(v) ? v : null; }
    function dstr(iso, style) {   // '2026-10-15' -> '15 Oct' or 'Oct 2026'
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
        if (!m) return '';
        const mo = MON[parseInt(m[2], 10) - 1];
        return style === 'month' ? mo + ' ' + m[1] : parseInt(m[3], 10) + ' ' + mo + (style === 'full' ? ' ' + m[1] : '');
    }
    function ago(iso) {
        const t = Date.parse(iso);
        if (isNaN(t)) return '';
        const m = Math.max(0, Math.round((Date.now() - t) / 60000));
        if (m < 2) return 'just now';
        if (m < 60) return m + ' min ago';
        const hr = Math.round(m / 60);
        return hr < 48 ? hr + ' h ago' : Math.round(hr / 24) + ' d ago';
    }
    function sgn(x, dp) { return (x > 0 ? '+' : x < 0 ? '−' : '') + Math.abs(x).toFixed(dp); }
    function dirOf(x, eps) { return Math.abs(x) < (eps || 0.005) ? 'flat' : x > 0 ? 'up' : 'down'; }
    function arrow(d) { return d === 'up' ? '▲' : d === 'down' ? '▼' : '–'; }

    function spark(values, cls) {
        const v = (values || []).filter(function (x) { return num(x) !== null; });
        const W = 84, H = 24, pad = 2;
        const s = document.createElementNS(NS, 'svg');
        s.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
        s.setAttribute('class', 'gh-mt-spark ' + (cls || ''));
        s.setAttribute('aria-hidden', 'true');
        if (v.length < 2) return s;
        let lo = Math.min.apply(null, v), hi = Math.max.apply(null, v);
        if (hi - lo < 1e-9) { hi += 1; lo -= 1; }
        const pts = v.map(function (y, i) {
            return [pad + i / (v.length - 1) * (W - 2 * pad), H - pad - (y - lo) / (hi - lo) * (H - 2 * pad)];
        });
        const p = document.createElementNS(NS, 'path');
        p.setAttribute('d', 'M' + pts.map(function (a) { return a[0].toFixed(1) + ' ' + a[1].toFixed(1); }).join(' L'));
        p.setAttribute('class', 'gh-mt-sparkline');
        s.appendChild(p);
        const e = pts[pts.length - 1], c = document.createElementNS(NS, 'circle');
        c.setAttribute('cx', e[0]); c.setAttribute('cy', e[1]); c.setAttribute('r', 2);
        c.setAttribute('class', 'gh-mt-sparkdot');
        s.appendChild(c);
        return s;
    }
    function empty(parent, text) { clear(parent); h('p', 'gh-mt-empty', text, parent); }

    // ---------- number formatting for FRED items ----------
    const PCT_KEYS = { policy: 1, y10: 1, rate: 1 };
    function isPct(it) { return !!PCT_KEYS[it.key] || /percent/i.test(it.units || ''); }
    function money(v, units) {
        const scale = /billion/i.test(units) ? 1e9 : /million/i.test(units) ? 1e6 : /thousand/i.test(units) ? 1e3 : 1;
        const sym = /euro/i.test(units) ? '€' : /yen/i.test(units) ? '¥' : /pound/i.test(units) ? '£' : '$';
        const x = Math.abs(v * scale);
        if (x >= 1e12) return sym + (v * scale / 1e12).toFixed(2) + ' tn';
        if (x >= 1e9) return sym + (v * scale / 1e9).toFixed(1) + ' bn';
        if (x >= 1e6) return sym + (v * scale / 1e6).toFixed(0) + ' m';
        return sym + (v * scale).toFixed(0);
    }
    function fmtVal(it, v) {
        if (isPct(it)) return v.toFixed(2) + '%';
        const u = it.units || '';
        if (/(million|billion|thousand)/i.test(u)) return money(v, u);
        if (it.key === 'fx') return v < 10 ? v.toFixed(4) : v < 1000 ? v.toFixed(2) : v.toFixed(0);
        if (/barrel/i.test(u)) return '$' + v.toFixed(2);
        return v < 100 ? v.toFixed(2) : v.toFixed(1);
    }
    function change(it) {   // change vs a year ago (or vs the previous reading when no year-ago value exists)
        const cur = it.last && num(it.last[1]);
        const ref = it.year_ago || it.prev;
        if (cur === null || !ref || num(ref[1]) === null) return null;
        const basis = it.year_ago ? 'yr' : 'prev';
        let txt, d;
        if (isPct(it)) { const x = cur - ref[1]; d = dirOf(x); txt = sgn(x, 2) + ' pp'; }
        else { if (!ref[1]) return null; const x = (cur / ref[1] - 1) * 100; d = dirOf(x, 0.05); txt = sgn(x, 1) + '%'; }
        return { txt: txt, dir: d, basis: basis, ref: ref[0] };
    }
    function itemDate(it) {
        if (!it.last) return '';
        return /month|quarter|annual|semi/i.test(it.freq || '') ? dstr(it.last[0], 'month') : dstr(it.last[0], 'short');
    }
    function visible(it) { return it && it.last && !(it.age_days != null && it.age_days > HIDE_AFTER_DAYS); }

    function deltaEl(parent, c, lbl) {
        if (!c) return h('span', 'gh-mt-delta is-flat', '', parent);
        const s = h('span', 'gh-mt-delta is-' + c.dir, arrow(c.dir) + ' ' + c.txt, parent);
        s.title = (c.basis === 'yr' ? 'Change since ' : 'Change since previous reading, ') + dstr(c.ref, 'full') + (lbl ? ' · ' + lbl : '');
        return s;
    }

    // ---------- headline tiles ----------
    function renderStats() {
        clear(els.stats);
        const tiles = [];
        if (D.macro && D.macro.global) {
            D.macro.global.items.forEach(function (it) {
                if (!visible(it)) return;
                const dcm = it.key === 'brent' ? dailyItem('BRENT') : it.key === 'wti' ? dailyItem('WTI') : null;
                if (dcm && dcm.age_days <= 7 && (it.age_days === undefined || it.age_days >= dcm.age_days)) tiles.push({ dc: dcm, label: it.label });
                else tiles.push({ it: it });
            });
        }
        const eu = D.agsi && (D.agsi.entities || []).filter(function (e) { return e.id === 'eu'; })[0];
        if (eu) tiles.push({ gas: eu });
        if (D.ofac && D.ofac.last_30d) tiles.push({ sx: D.ofac.last_30d });
        if (!tiles.length) { els.stats.hidden = true; return; }
        els.stats.hidden = false;
        tiles.forEach(function (t) {
            const c = h('div', 'gh-mt-tile', null, els.stats);
            if (t.dc) {
                dailyTile(c, t.dc, t.label);
            } else if (t.it) {
                h('div', 'gh-mt-tilelabel', t.it.label, c);
                h('div', 'gh-mt-tileval', fmtVal(t.it, t.it.last[1]), c);
                const row = h('div', 'gh-mt-tilerow', null, c);
                deltaEl(row, change(t.it), 'vs a year ago');
                row.appendChild(spark((t.it.spark || []).map(function (p) { return p[1]; })));
                h('div', 'gh-mt-tilesub', itemDate(t.it) + (t.it.age_days >= 3 && t.it.age_days <= 120 ? ' · ' + t.it.age_days + ' days old' : '') + (t.it.old ? ' · latest available' : ''), c);
            } else if (t.gas) {
                h('div', 'gh-mt-tilelabel', 'EU gas storage', c);
                h('div', 'gh-mt-tileval', t.gas.full.toFixed(1) + '% full', c);
                const row = h('div', 'gh-mt-tilerow', null, c);
                if (num(t.gas.year_ago_full) !== null) {
                    const x = t.gas.full - t.gas.year_ago_full;
                    const s = h('span', 'gh-mt-delta is-' + dirOf(x, 0.05), arrow(dirOf(x, 0.05)) + ' ' + sgn(x, 1) + ' pp', row);
                    s.title = 'Versus the same day last year (' + t.gas.year_ago_full.toFixed(1) + '%)';
                }
                row.appendChild(spark(t.gas.spark));
                h('div', 'gh-mt-tilesub', 'Gas day ' + dstr(t.gas.gas_day, 'short'), c);
            } else {
                h('div', 'gh-mt-tilelabel', 'US sanctions actions', c);
                h('div', 'gh-mt-tileval', String(t.sx.actions), c);
                h('div', 'gh-mt-tilesub', 'last 30 days · ' + t.sx.designations + ' with designations', c);
            }
        });
    }

    // ---------- metals and crypto (spot) ----------
    function spotPrice(it) {
        const p = it.price;
        if (p >= 1000) return '$' + p.toLocaleString('en-US', { maximumFractionDigits: p >= 10000 ? 0 : 2, minimumFractionDigits: p >= 10000 ? 0 : 2 });
        if (p < 0.01) return '$' + p.toFixed(Math.min(10, Math.ceil(-Math.log10(p)) + 2));
        if (p < 1) return '$' + p.toFixed(4);
        return '$' + p.toFixed(2);
    }
    function renderMarkets() {
        const m = D.mk;
        if (!m || !m.items || !m.items.length) { els.mkBox.hidden = true; return; }
        els.mkBox.hidden = false;
        els.mkAsof.textContent = 'Checked ' + ago(m.updated);
        clear(els.mk);
        m.items.forEach(function (it) {
            const c = h('div', 'gh-mt-tile', null, els.mk);
            h('div', 'gh-mt-tilelabel', it.name + (it.kind === 'crypto' ? ' · ' + it.sym : ' / ' + (it.unit || 'oz')), c);
            h('div', 'gh-mt-tileval', spotPrice(it), c);
            const row = h('div', 'gh-mt-tilerow', null, c);
            if (num(it.chg24_pct) !== null) {
                const d = dirOf(it.chg24_pct, 0.005);
                const s = h('span', 'gh-mt-delta is-' + d + (Math.abs(it.chg24_pct) >= 10 ? ' is-big' : ''), arrow(d) + ' ' + sgn(it.chg24_pct, 2) + '%', row);
                s.title = 'Change over the last 24 hours';
            } else { h('span', 'gh-mt-delta is-flat', '24h –', row); }
            row.appendChild(spark(it.spark));
            h('div', 'gh-mt-tilesub', (it.stale || it.old ? 'Delayed · last ' : 'Updated ') + ago(it.updated) + ' · ' + it.source, c);
        });
    }

    // ---------- daily commodities (Eulerpool) ----------
    function dailyItem(sym) {
        return ((D.dc && D.dc.items) || []).filter(function (i) { return i.sym === sym && !i.stale; })[0] || null;
    }
    function dailyPrice(it) {
        const p = it.price, u = it.unit || '';
        const cur = /^EUR/.test(u) ? '€' : /^CNY/.test(u) ? '¥' : '$';
        return cur + (p >= 1000 ? p.toLocaleString('en-US', { maximumFractionDigits: 0 }) : p >= 100 ? p.toFixed(1) : p >= 1 ? p.toFixed(2) : p.toFixed(3));
    }
    function dailyUnit(u) { return u ? u.replace(/^(USD|EUR|CNY)\//, '') : ''; }
    function dailyTile(c, it, label) {
        h('div', 'gh-mt-tilelabel', (label || it.label) + (it.unit ? ' · per ' + dailyUnit(it.unit) : ''), c);
        h('div', 'gh-mt-tileval', dailyPrice(it), c);
        const row = h('div', 'gh-mt-tilerow', null, c);
        if (num(it.chg_pct) !== null) {
            const d = dirOf(it.chg_pct, 0.005);
            const sp = h('span', 'gh-mt-delta is-' + d + (Math.abs(it.chg_pct) >= 10 ? ' is-big' : ''), arrow(d) + ' ' + sgn(it.chg_pct, 2) + '%', row);
            sp.title = 'Change since the previous daily reading';
        } else { h('span', 'gh-mt-delta is-flat', '–', row); }
        row.appendChild(spark(it.spark));
        const wk = num(it.wk_chg_pct) !== null ? ' · 1 week ' + sgn(it.wk_chg_pct, 1) + '%' : '';
        h('div', 'gh-mt-tilesub', dstr(it.date, 'short') + (it.age_days >= 3 ? ' · ' + it.age_days + ' days old' : '') + wk, c);
    }
    function renderDaily() {
        const d = D.dc;
        if (!d || !d.items || !d.items.length) { els.dcBox.hidden = true; return; }
        els.dcBox.hidden = false;
        clear(els.dc);
        const newest = d.items.map(function (i) { return i.date; }).sort().pop();
        els.dcAsof.textContent = 'Latest daily close ' + dstr(newest, 'short') + ' · checked ' + ago(d.updated);
        const groups = [];
        d.items.forEach(function (it) { if (groups.indexOf(it.group) === -1) groups.push(it.group); });
        groups.forEach(function (g) {
            h('h3', 'gh-mt-subhead', g, els.dc);
            const grid = h('div', 'gh-mt-spotgrid', null, els.dc);
            d.items.filter(function (it) { return it.group === g; }).forEach(function (it) {
                dailyTile(h('div', 'gh-mt-tile', null, grid), it);
            });
        });
        h('p', 'gh-mt-legend', 'Daily closing levels, usually one to two days behind. Uranium is a spot assessment that moves slowly, so it can stay flat for days. Not a live market feed.', els.dc);
    }

    // ---------- ten largest US companies (Eulerpool) ----------
    function bigMoney(v) {
        return v >= 1e12 ? '$' + (v / 1e12).toFixed(2) + ' trn' : '$' + (v / 1e9).toFixed(0) + ' bn';
    }
    function renderStocks() {
        const s = D.st;
        if (!s || !s.items || !s.items.length) { els.stBox.hidden = true; return; }
        els.stBox.hidden = false;
        clear(els.st);
        els.stAsof.textContent = 'Delayed quotes · checked ' + ago(s.updated);
        if (num(s.basket_chg_pct) !== null) {
            const top = h('div', 'gh-mt-gastop', null, els.st);
            const d = dirOf(s.basket_chg_pct, 0.005);
            const big = h('div', 'gh-mt-gasbig', null, top);
            h('span', 'gh-mt-gasnum', arrow(d) + ' ' + sgn(s.basket_chg_pct, 2) + '%', big);
            h('span', 'gh-mt-gaslbl', 'combined move today, ' + bigMoney(s.total_cap) + ' together', big);
        }
        const wrap = h('div', 'gh-mt-tablewrap', null, els.st);
        const t = h('table', 'gh-mt-table', null, wrap);
        h('caption', 'gh-mt-sr', 'Ten largest US companies by market capitalisation', t);
        const hr = h('tr', null, null, h('thead', null, null, t));
        [['#', ''], ['Company', ''], ['Price', 'num'], ['Today', 'num'], ['Market cap', 'num gh-mt-hide-s']].forEach(function (x) { h('th', x[1], x[0], hr).scope = 'col'; });
        const tb = h('tbody', null, null, t);
        s.items.forEach(function (it) {
            const tr = h('tr', null, null, tb);
            h('td', null, String(it.rank), tr);
            const nm = h('th', 'gh-mt-rowname', it.name, tr); nm.scope = 'row';
            h('span', 'gh-mt-rowdate', ' ' + it.ticker, nm);
            h('td', 'num', '$' + it.price.toFixed(2), tr);
            pctCell(h('td', 'num', null, tr), num(it.chg_pct));
            h('td', 'num gh-mt-hide-s', bigMoney(it.market_cap), tr);
        });
        h('p', 'gh-mt-legend', 'The largest US-listed companies are big enough to move indices, so their combined daily move is a quick read on market mood. Ranked by market capitalisation among a fixed shortlist of the biggest names. Quotes are delayed; not investment advice.', els.st);
    }

    // ---------- monthly commodity prices (World Bank) ----------
    const UNITS = { '$/mt': 'per tonne', '$/kg': 'per kg', '$/toz': 'per troy oz', '$/cum': 'per m³', '$/bbl': 'per barrel', '$/mmbtu': 'per mmBtu' };
    function cmPrice(v) {
        return '$' + (v >= 1000 ? v.toLocaleString('en-US', { maximumFractionDigits: 0 }) : v >= 100 ? v.toFixed(1) : v >= 10 ? v.toFixed(2) : v.toFixed(3));
    }
    function renderCommodities() {
        const c = D.cm;
        if (!c || !c.items || !c.items.length) { els.cmBox.hidden = true; return; }
        els.cmBox.hidden = false;
        clear(els.cm);
        const latest = c.items.map(function (i) { return i.last[0]; }).sort().pop();
        els.cmAsof.textContent = 'Month of ' + dstr(latest + '-01', 'month') + ' · checked ' + ago(c.checked || c.updated);
        const wrap = h('div', 'gh-mt-tablewrap', null, els.cm);
        const t = h('table', 'gh-mt-table', null, wrap);
        h('caption', 'gh-mt-sr', 'Monthly average commodity prices in US dollars', t);
        const hr = h('tr', null, null, h('thead', null, null, t));
        [['Commodity', ''], ['Price', 'num'], ['Unit', 'gh-mt-hide-s'], ['vs last month', 'num'], ['vs last year', 'num'], ['3 years', 'gh-mt-hide-s']].forEach(function (x) {
            h('th', x[1], x[0], hr).scope = 'col';
        });
        const tb = h('tbody', null, null, t);
        let group = null;
        c.items.forEach(function (it) {
            if (it.group !== group) {
                group = it.group;
                const g = h('tr', 'gh-mt-group', null, tb);
                const th = h('th', null, group, g); th.colSpan = 6; th.scope = 'colgroup';
            }
            const tr = h('tr', null, null, tb);
            const nm = h('th', 'gh-mt-rowname', it.label, tr); nm.scope = 'row';
            if (it.last[0] !== latest) { nm.title = 'Latest month available for this series: ' + dstr(it.last[0] + '-01', 'month'); h('span', 'gh-mt-rowdate is-old', ' (' + dstr(it.last[0] + '-01', 'month') + ')', nm); }
            h('td', 'num', cmPrice(it.last[1]), tr);
            h('td', 'gh-mt-hide-s', UNITS[it.unit] || it.unit, tr);
            pctCell(h('td', 'num', null, tr), it.prev ? (it.last[1] / it.prev[1] - 1) * 100 : null);
            pctCell(h('td', 'num', null, tr), it.year_ago ? (it.last[1] / it.year_ago[1] - 1) * 100 : null);
            h('td', 'gh-mt-hide-s', null, tr).appendChild(spark((it.spark || []).map(function (p) { return p[1]; })));
        });
        h('p', 'gh-mt-legend', 'Monthly averages published by the World Bank in the first days of each month, so the newest figure is always the previous month. Not a live market price.', els.cm);
    }

    // ---------- gas storage ----------
    function renderGas() {
        const a = D.agsi;
        if (!a || !a.entities || !a.entities.length) { empty(els.gas, 'Gas storage data is unavailable right now.'); els.gasAsof.textContent = ''; return; }
        clear(els.gas);
        els.gasAsof.textContent = 'Gas day ' + dstr((a.entities.filter(function (e) { return e.id === 'eu'; })[0] || a.entities[0]).gas_day, 'short') + ' · checked ' + ago(a.updated);
        const eu = a.entities.filter(function (e) { return e.id === 'eu'; })[0];
        if (eu) {
            const top = h('div', 'gh-mt-gastop', null, els.gas);
            const big = h('div', 'gh-mt-gasbig', null, top);
            h('span', 'gh-mt-gasnum', eu.full.toFixed(1) + '%', big);
            h('span', 'gh-mt-gaslbl', 'of EU storage full', big);
            const facts = h('ul', 'gh-mt-gasfacts', null, top);
            if (num(eu.twh) !== null && num(eu.capacity_twh)) h('li', null, eu.twh.toFixed(0) + ' of ' + eu.capacity_twh.toFixed(0) + ' TWh', facts);
            if (num(eu.year_ago_full) !== null) h('li', null, eu.year_ago_full.toFixed(1) + '% a year ago', facts);
            if (num(eu.trend) !== null) h('li', null, sgn(eu.trend, 2) + ' pp on the day', facts);
            const sp = h('div', 'gh-mt-gasspark', null, top);
            sp.appendChild(spark(eu.spark));
            h('span', null, '90 days', sp);
        }
        const list = h('ul', 'gh-mt-bars', null, els.gas);
        a.entities.filter(function (e) { return e.id !== 'eu'; }).sort(function (x, y) { return y.full - x.full; }).forEach(function (e) {
            const li = h('li', 'gh-mt-bar', null, list);
            h('span', 'gh-mt-barname', e.name, li);
            const track = h('span', 'gh-mt-track', null, li);
            const fill = h('span', 'gh-mt-fill', null, track);
            fill.style.width = Math.max(0, Math.min(100, e.full)) + '%';
            if (num(e.year_ago_full) !== null) {
                const tick = h('span', 'gh-mt-tick', null, track);
                tick.style.left = Math.max(0, Math.min(100, e.year_ago_full)) + '%';
                tick.title = 'Same day last year: ' + e.year_ago_full.toFixed(1) + '%';
            }
            const val = h('span', 'gh-mt-barval', e.full.toFixed(1) + '%', li);
            if (e.old || e.stale) val.title = 'Latest data: gas day ' + dstr(e.gas_day, 'full');
            li.appendChild(spark(e.spark));
        });
        h('p', 'gh-mt-legend', 'Bars show how full each country\'s storage is; the tick marks the same day last year.', els.gas);
    }

    // ---------- chokepoints ----------
    const SORTS = [['traffic', 'Busiest'], ['year', 'Change vs last year'], ['week', 'Change vs last week']];
    function renderChokeSort() {
        clear(els.chokeSort);
        SORTS.forEach(function (s) {
            const b = h('button', 'gh-mt-pill' + (chokeSort === s[0] ? ' is-active' : ''), s[1], els.chokeSort);
            b.type = 'button'; b.setAttribute('aria-pressed', chokeSort === s[0] ? 'true' : 'false');
            b.addEventListener('click', function () { chokeSort = s[0]; renderChokeSort(); renderChoke(); });
        });
    }
    function pctCell(td, x) {
        if (num(x) === null) { td.textContent = '–'; return; }
        const d = dirOf(x, 0.05);
        h('span', 'gh-mt-delta is-' + d + (Math.abs(x) >= 25 ? ' is-big' : ''), arrow(d) + ' ' + sgn(x, 1) + '%', td);
    }
    function renderChoke() {
        const p = D.pw;
        if (!p || !p.chokepoints || !p.chokepoints.length) { empty(els.choke, 'Chokepoint traffic is unavailable right now.'); els.chokeAsof.textContent = ''; els.chokeSort.hidden = true; return; }
        els.chokeSort.hidden = false;
        clear(els.choke);
        const latest = p.chokepoints.map(function (c) { return c.last_day; }).sort().pop();
        els.chokeAsof.textContent = 'Data to ' + dstr(latest, 'full') + ' · IMF publishes weekly';
        const key = { traffic: function (c) { return -(c.avg7 || 0); }, year: function (c) { return num(c.chg_year_pct) === null ? 1e9 : c.chg_year_pct; },
            week: function (c) { return num(c.chg_week_pct) === null ? 1e9 : c.chg_week_pct; } }[chokeSort];
        const rows = p.chokepoints.slice().sort(function (a, b) { return key(a) - key(b); });
        const wrap = h('div', 'gh-mt-tablewrap', null, els.choke);
        const t = h('table', 'gh-mt-table', null, wrap);
        h('caption', 'gh-mt-sr', 'Daily ship transits at major chokepoints', t);
        const hr = h('tr', null, null, h('thead', null, null, t));
        [['Chokepoint', ''], ['Ships / day', 'num'], ['vs last week', 'num'], ['vs last year', 'num'], ['Tankers', 'num gh-mt-hide-s'], ['90 days', 'gh-mt-hide-s']].forEach(function (c) {
            const th = h('th', c[1], c[0], hr); th.scope = 'col';
        });
        const tb = h('tbody', null, null, t);
        rows.forEach(function (c) {
            const tr = h('tr', c.old ? 'is-old' : '', null, tb);
            h('th', 'gh-mt-rowname', c.name.replace(/ (Strait|Canal)$/, ' $1'), tr).scope = 'row';
            const a = h('td', 'num', num(c.avg7) !== null ? c.avg7.toFixed(1) : '–', tr);
            if (num(c.daily) !== null) a.title = 'Latest day (' + dstr(c.last_day, 'short') + '): ' + c.daily;
            pctCell(h('td', 'num', null, tr), c.chg_week_pct);
            pctCell(h('td', 'num', null, tr), c.chg_year_pct);
            h('td', 'num gh-mt-hide-s', num(c.tanker_share_pct) !== null ? c.tanker_share_pct + '%' : '–', tr);
            h('td', 'gh-mt-hide-s', null, tr).appendChild(spark(c.spark));
        });
        h('p', 'gh-mt-legend', 'Ships per day is a 7-day average of vessel transit calls. "vs last year" compares the same 7-day window a year earlier.', els.choke);
    }

    // ---------- region filter ----------
    function renderFilter() {
        clear(els.filter);
        REGIONS.forEach(function (r) {
            const b = h('button', 'gh-mt-pill' + (region === r[0] ? ' is-active' : ''), r[1], els.filter);
            b.type = 'button'; b.setAttribute('aria-pressed', region === r[0] ? 'true' : 'false');
            b.addEventListener('click', function () { region = r[0]; renderFilter(); renderBanks(); renderSanctions(); });
        });
    }

    // ---------- central banks ----------
    function renderBanks() {
        const m = D.macro;
        if (!m || !m.countries) { empty(els.banks, 'Central-bank data is unavailable right now.'); els.banksAsof.textContent = ''; return; }
        clear(els.banks);
        const dates = [];
        m.countries.forEach(function (c) { (c.items || []).filter(visible).forEach(function (i) { dates.push(i.last[0]); }); });
        els.banksAsof.textContent = (dates.length ? 'Newest data ' + dstr(dates.sort().pop(), 'short') + ' · ' : '') + 'checked ' + ago(m.updated);
        const list = m.countries.filter(function (c) { return region === 'all' || (c.tags || []).indexOf(region) !== -1; });
        if (!list.length) { empty(els.banks, 'No countries in this region yet.'); return; }
        const grid = h('div', 'gh-mt-cards', null, els.banks);
        const sources = {};
        list.forEach(function (c) {
            const card = h('article', 'gh-mt-card', null, grid);
            const hd = h('header', 'gh-mt-cardhead', null, card);
            h('h3', null, c.name, hd);
            h('span', null, c.bank || '', hd);
            const items = (c.items || []).filter(visible);
            if (!items.length) { h('p', 'gh-mt-empty', 'No current data from our sources yet.', card); return; }
            const ul = h('ul', 'gh-mt-rows', null, card);
            items.forEach(function (it) {
                const li = h('li', 'gh-mt-row' + (it.old ? ' is-old' : ''), null, ul);
                const lab = h('span', 'gh-mt-rowlabel', it.label, li);
                const v = h('span', 'gh-mt-rowval', fmtVal(it, it.last[1]), li);
                deltaEl(li, change(it));
                li.appendChild(spark((it.spark || []).map(function (p) { return p[1]; })));
                const dt = h('span', 'gh-mt-rowdate', itemDate(it), li);
                if (it.old) { dt.title = 'The source has not updated this series recently; this is the latest reading available.'; dt.classList.add('is-old'); }
                lab.title = it.title || it.label;
                if (it.source) sources[it.source] = 1;
            });
        });
        const names = Object.keys(sources);
        if (els.srcs) els.srcs.textContent = names.length ? ' (including ' + names.sort().join('; ') + ')' : '';
        h('p', 'gh-mt-legend', 'Change is versus a year earlier (percentage points for rates and yields, percent otherwise). Dates in grey mean the source has not updated recently.', els.banks);
    }

    // ---------- sanctions ----------
    function renderSanctions() {
        const o = D.ofac;
        if (!o || !o.items) { empty(els.sx, 'Sanctions data is unavailable right now.'); els.sxAsof.textContent = ''; return; }
        clear(els.sx);
        els.sxAsof.textContent = (o.items[0] ? 'Latest action ' + dstr(o.items[0].date, 'short') + ' · ' : '') + 'checked ' + ago(o.updated);
        const items = o.items.filter(function (i) {
            return region === 'all' || (i.tags || []).indexOf(region) !== -1;
        }).slice(0, 12);
        if (!items.length) { empty(els.sx, 'No recent US sanctions actions tagged to this region.'); return; }
        const ul = h('ul', 'gh-mt-sxlist', null, els.sx);
        items.forEach(function (i) {
            const li = h('li', 'gh-mt-sx', null, ul);
            h('time', 'gh-mt-sxdate', dstr(i.date, 'short'), li).dateTime = i.date;
            const body = h('div', 'gh-mt-sxbody', null, li);
            const u = safeUrl(i.url);
            const t = h(u ? 'a' : 'span', 'gh-mt-sxtitle', i.title, body);
            if (u) { t.href = u; t.target = '_blank'; t.rel = 'noopener noreferrer'; }
            const meta = h('div', 'gh-mt-sxmeta', null, body);
            (i.kinds || [i.kind]).forEach(function (k) { if (KIND[k]) h('span', 'gh-mt-badge is-' + k, KIND[k], meta); });
            (i.tags || []).forEach(function (g) { if (TAGNAME[g]) h('span', 'gh-mt-tag', TAGNAME[g], meta); });
        });
    }

    // ---------- loading ----------
    function getJson(url) {
        if (!url) return Promise.resolve(null);
        return fetch(url, { cache: 'no-store' }).then(function (r) { if (!r.ok) throw 0; return r.json(); }).catch(function () { return null; });
    }
    function renderAll() {
        renderStats(); renderMarkets(); renderDaily(); renderStocks(); renderCommodities(); renderGas(); renderChokeSort(); renderChoke(); renderFilter(); renderBanks(); renderSanctions();
    }
    function load() {
        return Promise.all([getJson(root.dataset.macro), getJson(root.dataset.agsi), getJson(root.dataset.portwatch), getJson(root.dataset.ofac), getJson(root.dataset.markets), getJson(root.dataset.commodities), getJson(root.dataset.stocks), getJson(root.dataset.daily)])
            .then(function (r) { D.macro = r[0]; D.agsi = r[1]; D.pw = r[2]; D.ofac = r[3]; D.mk = r[4]; D.cm = r[5]; D.st = r[6]; D.dc = r[7]; renderAll(); });
    }
    load();
    setInterval(function () { if (!document.hidden) load(); }, (parseInt(root.dataset.refresh, 10) || 900) * 1000);
})();
